/**
 * Custom note templates — persistence helpers.
 *
 * Templates live in an array the server replaces wholesale on every write:
 * `user.custom.customNotes` (per user) or `orgInfo.customNotes` (shared, on
 * Ultimate plans). Both records are routinely re-saved in full by unrelated
 * screens (Settings profile save, "enable this note type", every
 * `{ ...auth.orgInfo, x }` org save) from whatever snapshot that tab, the Chrome
 * extension panel, the phone webview or a colleague's session loaded at login.
 * Any such save echoed a stale array over templates built since — they looked
 * "auto-deleted" within the hour.
 *
 * Rule enforced here: ONLY the custom-notes screen writes `customNotes`, and it
 * does so on top of the freshest server copy. Everything else has the key
 * stripped at the API layer (see api.updateProfile / api.updateOrgInfo).
 */

const CUSTOM_NOTES_KEY = "customNotes";

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

/**
 * Apply one change to the server's current list and return the new list.
 * @param {Array|null|undefined} serverNotes - freshest list read from the server
 * @param {{type:"upsert", note:Object}|{type:"delete", id:string}} op
 */
export function applyCustomNoteOp(serverNotes, op) {
  const base = Array.isArray(serverNotes) ? serverNotes : [];
  if (!op) return [...base];
  if (op.type === "delete") return base.filter((n) => n?.id !== op.id);
  if (op.type === "upsert" && op.note) {
    const idx = base.findIndex((n) => n?.id === op.note.id);
    if (idx === -1) return [...base, op.note];
    const existing = base[idx];
    const merged = { ...op.note, createdAt: existing?.createdAt ?? op.note.createdAt };
    return base.map((n, i) => (i === idx ? merged : n));
  }
  return [...base];
}

/**
 * Flag every template in `notes` as enabled so it appears in the note picker
 * without the "New Notes" modal. Returns a new plain object; a legacy `[]`
 * value (seeded by Settings.fetchProfile) is normalized, because flags set on
 * an array are dropped by JSON.stringify and never reached the server.
 */
export function enableCustomNotes(availableNotes, notes) {
  const next = isPlainObject(availableNotes) ? { ...availableNotes } : {};
  for (const n of notes || []) {
    if (n?.id) next["custom:" + n.id] = true;
  }
  return next;
}

/** Copy of a user-profile payload without `custom.customNotes`. Never mutates the input. */
export function omitCustomNotesFromProfile(profile) {
  if (!isPlainObject(profile) || !isPlainObject(profile.custom) || !(CUSTOM_NOTES_KEY in profile.custom)) {
    return profile;
  }
  const { [CUSTOM_NOTES_KEY]: _omit, ...custom } = profile.custom;
  return { ...profile, custom };
}

/** Copy of an org payload without the top-level `customNotes`. Never mutates the input. */
export function omitCustomNotesFromOrg(orgInfo) {
  if (!isPlainObject(orgInfo) || !(CUSTOM_NOTES_KEY in orgInfo)) return orgInfo;
  const { [CUSTOM_NOTES_KEY]: _omit, ...rest } = orgInfo;
  return rest;
}
